import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CloudinaryUploadService } from './thirdparties/cloudinary-upload.service';
import { WineryInfo } from '@interfaces/dws/winery-info';
import { IntegrationEntityTypes } from '@interfaces/integration-sync-metadata';
import { resource } from '@util/resource';

export interface CompanyImageUpload {
  logo?: File;
  banner?: File;
  cover?: File;
}

export interface CompanyImageUrls {
  logo?: string;
  banner?: string;
  cover?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyImagesService {

  constructor(private cloudinaryUploadService: CloudinaryUploadService) {}

  uploadCompanyImages(wineryId: string, images: CompanyImageUpload): Observable<CompanyImageUrls> {
    const uploadPromises: Promise<any>[] = [];
    const result: CompanyImageUrls = {};

    if (images.logo) {
      uploadPromises.push(
        this.cloudinaryUploadService.uploadImage(images.logo, wineryId, 'logo', wineryId, IntegrationEntityTypes.WINERY).toPromise()
          .then(response => {
            result.logo = this.cloudinaryUploadService.getImageUrl(response.public_id);
          })
      );
    }

    if (images.banner) {
      uploadPromises.push(
        this.cloudinaryUploadService.uploadImage(images.banner, wineryId, 'banner', wineryId, IntegrationEntityTypes.WINERY).toPromise()
          .then(response => {
            result.banner = this.cloudinaryUploadService.getImageUrl(response.public_id);
          })
      );
    }

    if (images.cover) {
      uploadPromises.push(
        this.cloudinaryUploadService.uploadImage(images.cover, wineryId, 'cover', wineryId, IntegrationEntityTypes.WINERY).toPromise()
          .then(response => {
            result.cover = this.cloudinaryUploadService.getImageUrl(response.public_id);
          })
      );
    }

    return new Observable(observer => {
      Promise.all(uploadPromises)
        .then(() => {
          observer.next(result);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  getImageUrl(publicId: string): string {
    return this.cloudinaryUploadService.getImageUrl(publicId);
  }

  getImageUrlWithTransformation(publicId: string, transformation: string): string {
    return this.cloudinaryUploadService.getImageUrlWithTransformation(publicId, transformation);
  }

  getImageUrlWithSize(publicId: string, width: number, height: number): string {
    return this.cloudinaryUploadService.getImageUrlWithTransformationAndSize(publicId, 'c_fill', width, height);
  }

  // New method for uploading 3 photos together
  uploadThreePhotos(wineryId: string, images: CompanyImageUpload): Observable<CompanyImageUrls> {
    const uploadPromises: Promise<any>[] = [];
    const result: CompanyImageUrls = {};

    if (images.logo) {
      uploadPromises.push(
        this.cloudinaryUploadService.uploadImage(images.logo, wineryId, 'logo', wineryId, IntegrationEntityTypes.WINERY).toPromise()
          .then(response => {
            result.logo = this.cloudinaryUploadService.getImageUrl(response.public_id);
          })
      );
    }

    if (images.banner) {
      uploadPromises.push(
        this.cloudinaryUploadService.uploadImage(images.banner, wineryId, 'banner', wineryId, IntegrationEntityTypes.WINERY).toPromise()
          .then(response => {
            result.banner = this.cloudinaryUploadService.getImageUrl(response.public_id);
          })
      );
    }

    if (images.cover) {
      uploadPromises.push(
        this.cloudinaryUploadService.uploadImage(images.cover, wineryId, 'cover', wineryId, IntegrationEntityTypes.WINERY).toPromise()
          .then(response => {
            result.cover = this.cloudinaryUploadService.getImageUrl(response.public_id);
          })
      );
    }

    return new Observable(observer => {
      Promise.all(uploadPromises)
        .then(() => {
          observer.next(result);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  // Download company images by winery ID - returns public IDs
  downloadCompanyImages(wineryId: string): Observable<CompanyImagePublicIds> {
    return resource('crm://winery/wineryId/images')
      .params({ wineryId })
      .get<CompanyImagePublicIds>();
  }
}

export interface CompanyImagePublicIds {
  logo?: string;
  banner?: string;
  cover?: string;
}
